import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Tabs, Spin, Button, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { loadConnectAndInitialize, StripeConnectInstance } from '@stripe/connect-js';
import { ConnectComponentsProvider, ConnectAccountOnboarding, ConnectBalances, ConnectPayouts, ConnectPayments } from '@stripe/react-connect-js';
import { startHostVerification } from '../../../services/HostProfileService';
import { getStripeConfig } from '../../../services/StripeService';
import { useHostProfile } from '../../../contextProviders/HostProfileProvider';
import SettingsPillBadge, { SettingsPillBadgeTone } from '../../../components/Settings/SettingsPillBadge';
import { CONNECT_APPEARANCE } from '../../../constants/stripeConnectAppearance';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';

const { Text } = Typography;

const STATUS_TONE: Record<string, SettingsPillBadgeTone> = {
  NotStarted: 'default',
  Pending: 'processing',
  Verified: 'success',
  Rejected: 'error',
};

const STATUS_LABEL_KEY: Record<string, string> = {
  NotStarted: TRANSLATIONS.SETTINGS_PAYOUTS_STATUS_NOT_STARTED,
  Pending: TRANSLATIONS.SETTINGS_PAYOUTS_STATUS_PENDING,
  Verified: TRANSLATIONS.SETTINGS_PAYOUTS_STATUS_VERIFIED,
  Rejected: TRANSLATIONS.SETTINGS_PAYOUTS_STATUS_REJECTED,
};

/**
 * "Configuración > Pagos y cobros > Cobros" -- the host's own Stripe Connect
 * Express onboarding AND, once Verified, their balance/payouts/payments,
 * all embedded via Connect.js (see CONNECT_APPEARANCE) so the host never
 * leaves this page for a stripe.com one. Reuses useHostProfile so the status
 * shown here is always the same one everywhere else in the app reads; the
 * actual Verified/Rejected transition itself comes from Stripe's own
 * account.updated webhook, never from anything on this page.
 */
const PayoutsTab: React.FC = () => {
  const { t } = useTranslation();
  const { hostProfile, hasHostProfile, loading, refresh } = useHostProfile();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(false);
  const [connectInstance, setConnectInstance] = useState<StripeConnectInstance | undefined>(undefined);

  const status = hostProfile?.idVerificationStatus ?? 'NotStarted';

  const initializeConnect = useCallback(async () => {
    setStarting(true);
    setError(false);
    try {
      const { publishableKey } = await getStripeConfig();
      const instance = loadConnectAndInitialize({
        publishableKey,
        // Called again by Connect.js itself whenever a fresh session is
        // needed (the first one included, plus any later refresh) -- this
        // is also what creates the host's Express account server-side the
        // very first time it's called. The same session/instance serves
        // <ConnectAccountOnboarding> and, once Verified, the
        // balances/payouts/payments components below.
        fetchClientSecret: async () => {
          const result = await startHostVerification();
          return result.accountSessionClientSecret ?? '';
        },
        appearance: CONNECT_APPEARANCE,
      });
      setConnectInstance(instance);
    } catch {
      setError(true);
    } finally {
      setStarting(false);
    }
  }, []);

  // An account already exists past NotStarted (Pending/Rejected/Verified),
  // so there's no side effect to gate behind an explicit click -- initialize
  // Connect.js as soon as the tab opens. NotStarted stays behind the button
  // below, since that first call is what actually creates the Express
  // account server-side.
  useEffect(() => {
    if (hasHostProfile && status !== 'NotStarted' && !connectInstance && !starting) {
      initializeConnect();
    }
  }, [hasHostProfile, status, connectInstance, starting, initializeConnect]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 16 }}>
        <Spin />
      </div>
    );
  }

  if (!hasHostProfile) {
    return <Text type="secondary">{translate(t, TRANSLATIONS.SETTINGS_PAYOUTS_NOT_HOST)}</Text>;
  }

  return (
    <div>
      {/* flexWrap so a long status pill (e.g. Rejected's full sentence) drops
          to its own line on narrow screens instead of squeezing the label
          next to it down toward zero width -- without this, the label's
          text wraps one character per line once it has no room left. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Text strong style={{ flexShrink: 0 }}>
          {translate(t, TRANSLATIONS.SETTINGS_PAYOUTS_STATUS_LABEL)}
        </Text>
        <SettingsPillBadge tone={STATUS_TONE[status]}>{translate(t, STATUS_LABEL_KEY[status])}</SettingsPillBadge>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translate(t, TRANSLATIONS.SETTINGS_PAYOUTS_ERROR)}
        />
      )}

      {status === 'Verified' && <Text type="secondary">{translate(t, TRANSLATIONS.SETTINGS_PAYOUTS_VERIFIED_MESSAGE)}</Text>}

      {connectInstance ? (
        // width:'100%' gives the embedded iframe a real, stable box to
        // measure on its very first render -- without it, it can briefly
        // render at (or resize up from) an almost-zero width before its own
        // content settles, which reads as text wrapping one character per
        // line for an instant (same class of bug this page's own mobile
        // layout had, see Settings.tsx's comment on that fix).
        <div style={{ width: '100%' }}>
          <ConnectComponentsProvider connectInstance={connectInstance}>
            {status === 'Verified' ? (
              <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 24 }}>
                <ConnectBalances />
                <ConnectPayouts />
                <ConnectPayments />
              </div>
            ) : (
              <ConnectAccountOnboarding onExit={() => refresh()} />
            )}
          </ConnectComponentsProvider>
        </div>
      ) : status === 'NotStarted' ? (
        <Button shape="round" type="primary" loading={starting} onClick={initializeConnect}>
          {translate(t, TRANSLATIONS.SETTINGS_PAYOUTS_START_BUTTON)}
        </Button>
      ) : (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin />
        </div>
      )}
    </div>
  );
};

/**
 * "Configuración > Pagos y cobros" -- "Cobros" is the host's real Stripe
 * Connect onboarding (see PayoutsTab); "Pagos" (what a guest paid for a
 * booking) has no history view yet, still a placeholder. No page title here
 * on purpose -- Settings' own sidebar item already says the same thing.
 */
const PaymentsSection: React.FC = () => {
  const { t } = useTranslation();
  const paymentsPlaceholder = <Text type="secondary">{translate(t, TRANSLATIONS.SETTINGS_PAYMENTS_COMING_SOON)}</Text>;

  return (
    <Tabs
      items={[
        { key: 'payments', label: translate(t, TRANSLATIONS.SETTINGS_PAYMENTS_TAB_PAYMENTS), children: paymentsPlaceholder },
        { key: 'payouts', label: translate(t, TRANSLATIONS.SETTINGS_PAYMENTS_TAB_PAYOUTS), children: <PayoutsTab /> },
      ]}
    />
  );
};

export default PaymentsSection;
